import { SxProps, Theme } from "@mui/material";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { focusRing } from "theme/colors";
import workspacesTokens from "./Workspaces.tokens";

const t = workspacesTokens;
const list = t.list;

type PillTone = { bg: string; text: string };

export const visuallyHiddenSx: SxProps<Theme> = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const focusVisible = {
  outline: `2px solid ${focusRing}`,
  outlineOffset: "2px",
} as const;

/* ── Page chrome ─────────────────────────────────────────────────────────── */

/** "← Back to workspaces" — quiet text link above the sub-page's card. */
export const backLinkSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  gap: t.backLink.gap,
  mb: t.backLink.marginBottom,
  p: 0,
  border: "none",
  bgcolor: "transparent",
  font: "inherit",
  fontSize: t.backLink.fontSize,
  fontWeight: t.backLink.fontWeight,
  color: t.backLink.color,
  lineHeight: "normal",
  cursor: "pointer",
  "& svg": { fontSize: t.backLink.iconSize },
  "&:hover": { color: t.backLink.hoverColor },
  "&:focus-visible": { ...focusVisible, borderRadius: "4px" },
};

/** Header action cluster: the archived toggle and the primary action. */
export const headerActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  flexWrap: "wrap",
  width: { xs: "100%", md: "auto" },
};

export const headerToggleSx: SxProps<Theme> = {
  m: 0,
  gap: "8px",
  "& .MuiFormControlLabel-label": {
    fontSize: t.section.descriptionFontSize,
    fontWeight: 600,
    color: t.backLink.color,
  },
};

/**
 * Primary page action. Chrome comes from the global button theme; only the
 * settings screens' phone layout is added — full width at 44px below `md`.
 */
export const primaryButtonSx: SxProps<Theme> = {
  width: { xs: "100%", md: "auto" },
  height: { xs: "44px", md: undefined },
  whiteSpace: "nowrap",
  flexShrink: 0,
};

/** White rounded card — same chrome as every other Settings card. */
export const cardSx: SxProps<Theme> = {
  bgcolor: t.card.bg,
  border: `1px solid ${t.card.border}`,
  borderRadius: t.card.radius,
  boxShadow: t.card.shadow,
  boxSizing: "border-box",
  overflow: "hidden",
};

/** Card whose body is a form: padded, blocks stacked. */
export const formCardSx: SxProps<Theme> = {
  bgcolor: t.card.bg,
  border: `1px solid ${t.card.border}`,
  borderRadius: t.card.radius,
  boxShadow: t.card.shadow,
  boxSizing: "border-box",
  p: t.card.padding,
  display: "flex",
  flexDirection: "column",
  gap: t.card.gap,
};

export const pageStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.card.stackGap,
};

export const sectionHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "14px",
};

export const sectionHeaderTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.section.textGap,
  minWidth: 0,
};

export const sectionTitleSx: SxProps<Theme> = {
  color: t.section.titleColor,
  fontSize: t.section.titleFontSize,
  fontWeight: t.section.titleFontWeight,
  letterSpacing: t.section.titleLetterSpacing,
  lineHeight: "normal",
};

export const sectionDescriptionSx: SxProps<Theme> = {
  color: t.section.descriptionColor,
  fontSize: t.section.descriptionFontSize,
  lineHeight: t.section.descriptionLineHeight,
};

export const sectionDividerSx: SxProps<Theme> = {
  borderTop: `1px solid ${t.section.divider}`,
  pt: t.card.gap,
};

/** Gate banner inset into a list card, above the column strip. */
export const listBannerSx: SxProps<Theme> = {
  px: list.space.gutter,
  py: "16px",
};

/** Info note, e.g. "roles are shared across every workspace". */
export const noteSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: t.note.gap,
  p: t.note.padding,
  borderRadius: t.note.radius,
  bgcolor: t.note.bg,
  border: `1px solid ${t.note.border}`,
  color: t.note.text,
  fontSize: t.note.fontSize,
  lineHeight: t.note.lineHeight,
  "& strong": { color: t.note.strong, fontWeight: 700 },
  "& > svg": { color: t.note.icon, fontSize: t.note.iconSize, mt: "1px" },
};

/** Same note, toned for an inline warning / blocking error. */
export const alertSx = (tone: "warning" | "error"): SxProps<Theme> => {
  const c = t.alert[tone];
  return {
    display: "flex",
    alignItems: "flex-start",
    gap: t.note.gap,
    p: t.note.padding,
    borderRadius: t.note.radius,
    bgcolor: c.bg,
    border: `1px solid ${c.border}`,
    color: c.text,
    fontSize: t.note.fontSize,
    lineHeight: t.note.lineHeight,
    "& strong": { color: c.title, fontWeight: 700 },
    "& > svg": { color: c.icon, fontSize: t.note.iconSize, mt: "1px" },
  };
};

/* ── List (Integrations' strip + rows) ───────────────────────────────────── */

export const columnHeaderSx = (columns: string): SxProps<Theme> => ({
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: columns,
  columnGap: "16px",
  px: list.space.gutter,
  py: list.space.columnHeaderY,
  bgcolor: list.color.headerBg,
  borderBottom: `1px solid ${list.color.headerBorder}`,
  fontSize: list.font.columnHeader,
  fontWeight: list.weight.strong,
  letterSpacing: list.letterSpacing.columnHeader,
  color: list.color.muted,
  lineHeight: "normal",
  textTransform: "uppercase",
});

/**
 * One row. A grid on desktop; below `md` the cells wrap — the first (name)
 * cell takes the full line and the rest sit under it.
 */
export const listRowSx = (columns: string, isLast: boolean): SxProps<Theme> => ({
  display: { xs: "flex", md: "grid" },
  flexWrap: "wrap",
  gridTemplateColumns: { md: columns },
  alignItems: "center",
  columnGap: { xs: list.space.stackGap, md: "16px" },
  rowGap: list.space.stackGap,
  px: list.space.gutter,
  py: list.space.rowY,
  borderBottom: isLast ? "none" : `1px solid ${list.color.rowDivider}`,
  "& > :first-of-type": { flexBasis: { xs: "100%", md: "auto" } },
});

export const nameCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: list.space.nameGap,
  minWidth: 0,
};

export const rowTextSx = (
  tone: "ink" | "body",
  weight: "strong" | "regular",
): SxProps<Theme> => ({
  fontSize: list.font.rowText,
  fontWeight: list.weight[weight],
  color: list.color[tone],
  lineHeight: "normal",
  minWidth: 0,
  wordBreak: "break-word",
});

/** A workspace name that opens its members — styled as plain row text. */
export const nameButtonSx: SxProps<Theme> = {
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  font: "inherit",
  fontSize: list.font.rowText,
  fontWeight: list.weight.strong,
  color: list.color.ink,
  lineHeight: "normal",
  textAlign: "left",
  wordBreak: "break-word",
  cursor: "pointer",
  "&:hover": { color: list.color.accent },
  "&:focus-visible": { ...focusVisible, borderRadius: "3px" },
};

export const pillSx = (tone: PillTone): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  p: list.pillPadding,
  borderRadius: list.pillRadius,
  bgcolor: tone.bg,
  color: tone.text,
  fontSize: list.font.pill,
  fontWeight: list.weight.strong,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
});

export const actionCellSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: list.actionGap,
  ml: { xs: "auto", md: 0 },
};

/** Icon action in a row (kebab, edit, view). */
export const rowActionSx: SxProps<Theme> = {
  width: list.actionSize,
  height: list.actionSize,
  borderRadius: "8px",
  color: list.color.body,
  "& svg": { fontSize: list.actionIconSize },
  "&:hover": { bgcolor: list.color.actionHover, color: list.color.ink },
  "&.Mui-focusVisible": focusVisible,
};

/** Destructive icon action in a row (remove, delete). */
export const rowDangerActionSx: SxProps<Theme> = {
  width: list.actionSize,
  height: list.actionSize,
  borderRadius: "8px",
  color: list.color.body,
  "& svg": { fontSize: list.actionIconSize },
  "&:hover": { bgcolor: list.color.dangerHoverBg, color: list.color.danger },
  "&.Mui-focusVisible": focusVisible,
};

/* ── Selects ─────────────────────────────────────────────────────────────── */

const s = t.select;

const selectBase = {
  width: "100%",
  bgcolor: s.bg,
  color: s.color,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: s.border,
    borderWidth: "1px",
  },
  "&:hover": {
    bgcolor: s.bg,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: s.border },
  },
  "&.Mui-focused": {
    bgcolor: s.bg,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: s.focusBorder,
      borderWidth: "1px",
    },
  },
  "&.Mui-disabled": { bgcolor: s.disabledBg },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    minHeight: "unset",
  },
  "& .MuiSelect-icon": { right: 10, color: s.caret },
} as const;

/** Form select — the shared 44px field box, matching `CustomInput`. */
export const formSelectSx: SxProps<Theme> = {
  ...selectBase,
  height: s.height,
  borderRadius: s.radius,
  fontSize: s.fontSize,
  fontWeight: s.fontWeight,
  "& .MuiSelect-select": {
    ...selectBase["& .MuiSelect-select"],
    p: `0 ${s.paddingX}`,
    pr: "36px !important",
  },
};

/** Compact select inside a list row. */
export const rowSelectSx: SxProps<Theme> = {
  ...selectBase,
  width: { xs: "auto", md: "100%" },
  minWidth: { xs: 180, md: 0 },
  height: t.rowSelect.height,
  borderRadius: t.rowSelect.radius,
  fontSize: t.rowSelect.fontSize,
  fontWeight: t.rowSelect.fontWeight,
  "& .MuiSelect-select": {
    ...selectBase["& .MuiSelect-select"],
    p: `0 ${t.rowSelect.paddingX}`,
    pr: "34px !important",
  },
};

export const selectPlaceholderSx: SxProps<Theme> = {
  color: s.placeholder,
};

/* ── Modal bodies ────────────────────────────────────────────────────────── */

export const modalBodyTextSx: SxProps<Theme> = {
  color: t.modal.bodyColor,
  fontSize: t.modal.bodyFontSize,
  lineHeight: t.modal.bodyLineHeight,
  "& strong": { color: t.section.titleColor },
};

export const modalFieldsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.modal.fieldGap,
};

/**
 * Validation line under a modal field — the same error red and helper-text
 * size `CustomInput` uses for its own field errors.
 */
export const fieldHintSx: SxProps<Theme> = {
  mt: "6px",
  color: "error.main",
  fontSize: formFieldMetrics.helperFontSize,
  lineHeight: 1.45,
};

export const groupLabelSx: SxProps<Theme> = {
  color: t.modal.groupLabelColor,
  fontSize: t.modal.groupLabelFontSize,
  fontWeight: t.modal.groupLabelFontWeight,
  lineHeight: "normal",
  mb: "6px",
};

export const checkboxGroupSx: SxProps<Theme> = {
  gap: t.modal.checkboxGap,
};

export const checkboxLabelSx: SxProps<Theme> = {
  m: 0,
  "& .MuiFormControlLabel-label": {
    color: t.modal.checkboxLabelColor,
    fontSize: t.modal.checkboxLabelFontSize,
    fontWeight: t.modal.checkboxLabelFontWeight,
  },
};

/* ── Form cards (details, branding) ──────────────────────────────────────── */

export const formFieldsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.form.fieldGap,
  maxWidth: t.form.maxWidth,
};

export const buttonRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-start",
  gap: t.form.buttonRowGap,
  flexWrap: "wrap",
};

export const dangerDescriptionSx: SxProps<Theme> = {
  color: t.section.descriptionColor,
  fontSize: t.section.descriptionFontSize,
  lineHeight: t.section.descriptionLineHeight,
  mb: "12px",
};

export const dangerTitleSx: SxProps<Theme> = {
  color: t.section.titleColor,
  fontSize: t.form.dangerTitleFontSize,
  fontWeight: 700,
  lineHeight: "normal",
  mb: "4px",
};

/* ── Branding ────────────────────────────────────────────────────────────── */

const chip = t.colorChip;

export const colorRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-end",
  gap: t.form.columnGap,
  flexWrap: "wrap",
};

/** Hex read-out chip; doubles as the trigger for the native colour picker. */
export const colorChipSx = (disabled: boolean): SxProps<Theme> => ({
  position: "relative",
  height: chip.height,
  px: chip.paddingX,
  display: "inline-flex",
  alignItems: "center",
  gap: chip.gap,
  boxSizing: "border-box",
  border: `1px solid ${chip.border}`,
  borderRadius: chip.radius,
  bgcolor: chip.bg,
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.6 : 1,
  "&:focus-within": focusVisible,
});

export const colorChipDotSx = (color: string): SxProps<Theme> => ({
  width: chip.dotSize,
  height: chip.dotSize,
  flexShrink: 0,
  borderRadius: chip.dotRadius,
  border: `1px solid ${chip.dotBorder}`,
  boxSizing: "border-box",
  bgcolor: color,
});

export const colorChipValueSx: SxProps<Theme> = {
  fontSize: chip.fontSize,
  fontWeight: chip.fontWeight,
  color: chip.color,
  lineHeight: "normal",
  textTransform: "uppercase",
};

/** Invisible native picker filling the chip, so the OS popup anchors to it. */
export const colorInputSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  p: 0,
  border: "none",
  opacity: 0,
  cursor: "inherit",
};

/** Live preview of the signing-page button in the chosen colours. */
export const buttonPreviewSx = (bg: string, color: string): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: chip.previewHeight,
  px: chip.previewPaddingX,
  boxSizing: "border-box",
  borderRadius: chip.previewRadius,
  bgcolor: bg,
  color,
  fontSize: chip.previewFontSize,
  fontWeight: chip.previewFontWeight,
});

export const logoRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.logo.gap,
  flexWrap: "wrap",
};

export const logoBoxSx: SxProps<Theme> = {
  width: t.logo.boxWidth,
  height: t.logo.boxHeight,
  p: t.logo.padding,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: t.logo.radius,
  border: `1px solid ${t.logo.border}`,
  bgcolor: t.logo.bg,
  color: t.logo.emptyColor,
  fontSize: t.logo.emptyFontSize,
  "& img": { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
};
